import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    InputAdornment,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import {
    Edit as EditIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    AdminPanelSettings as RoleIcon,
    CheckCircle as CheckCircleIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    VpnKey as ResetPasswordIcon,
    LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { request } from '../../../../../utils/request';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

// Types
interface User {
    _id: string;
    name: string;
    userName: string;
    email: string;
    phoneNumber?: string;
    address?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
    };
    role: {
        _id: string;
        name: string;
        description?: string;
    };
    status: "pending" | "active" | "suspended" | "deactivated" | "deleted";
    isEmailVerified: boolean;
    createdAt: string;
    updatedAt: string;
}

interface Role {
    _id: string;
    name: string;
    description?: string;
}

interface UserFormData {
    name: string;
    userName: string;
    email: string;
    phoneNumber: string;
    address: {
        line1: string;
        line2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    };
    role: string;
    status: "pending" | "active" | "suspended" | "deactivated" | "deleted";
}

const UserList: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reset password states
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [resettingPassword, setResettingPassword] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<UserFormData>({
        defaultValues: {
            name: '',
            userName: '',
            email: '',
            phoneNumber: '',
            address: {
                line1: '',
                line2: '',
                city: '',
                state: '',
                zip: '',
                country: '',
            },
            role: '',
            status: 'pending',
        },
    });

    // Fetch all users
    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await request({
                url: 'auth/get-all-users',
                method: 'get',
            });

            const usersData = response.data.users;
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error: any) {
            console.error('Error fetching users:', error);
            toast.error(error?.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch all roles
    const fetchRoles = useCallback(async () => {
        try {
            const response = await request({
                url: 'role/get-all-roles',
                method: 'get',
            });

            const rolesData = response.data || response.roles || response;
            setRoles(Array.isArray(rolesData) ? rolesData : []);
        } catch (error: any) {
            console.error('Error fetching roles:', error);
            toast.error(error?.message || 'Failed to load roles');
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [fetchUsers, fetchRoles]);

    // Open dialog for add/edit
    const openDialog = (user?: User) => {
        if (user) {
            setEditingUser(user);
            reset({
                name: user.name,
                userName: user.userName,
                email: user.email,
                phoneNumber: user.phoneNumber || '',
                address: {
                    line1: user.address?.line1 || '',
                    line2: user.address?.line2 || '',
                    city: user.address?.city || '',
                    state: user.address?.state || '',
                    zip: user.address?.zip || '',
                    country: user.address?.country || '',
                },
                role: user.role?._id || '',
                status: user.status,
            });
        } else {
            setEditingUser(null);
            reset({
                name: '',
                userName: '',
                email: '',
                phoneNumber: '',
                address: {
                    line1: '',
                    line2: '',
                    city: '',
                    state: '',
                    zip: '',
                    country: '',
                },
                role: '',
                status: 'pending',
            });
        }
        setDialogOpen(true);
    };

    // Close dialog
    const closeDialog = () => {
        setDialogOpen(false);
        setEditingUser(null);
        reset();
    };

    // Handle create/update user
    const onSubmit = async (data: UserFormData) => {
        try {
            setSaving(true);

            const payload = {
                name: data.name,
                userName: data.userName,
                email: data.email,
                phoneNumber: data.phoneNumber,
                address: data.address,
                role: data.role,
                status: data.status,
            };

            let response;
            if (editingUser) {
                // Remove email from payload when updating (email cannot be changed)
                const { email, ...updatePayload } = payload;
                response = await request({
                    url: `auth/update-user/${editingUser._id}`,
                    method: 'put',
                    data: updatePayload,
                });
            } else {
                response = await request({
                    url: 'auth/register',
                    method: 'post',
                    data: {
                        ...payload,
                        password: 'Temp@123', // Temporary password for new users
                    },
                });
            }

            if (response.status === 'success') {
                toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
                closeDialog();
                fetchUsers();
            } else {
                toast.error(response.message || 'Operation failed');
            }
        } catch (error: any) {
            console.error('Error saving user:', error);
            toast.error(error?.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    // Handle reset password
    const handleResetPassword = (user: User) => {
        setSelectedUser(user);
        setResetPasswordOpen(true);
    };

    const confirmResetPassword = async () => {
        if (!selectedUser) return;

        try {
            setResettingPassword(true);

            const response = await request({
                url: `admin/reset-password/${selectedUser._id}`,
                method: 'post',
            });

            if (response.status === 'success') {
                toast.success(response.message || `Password reset successfully. A new password has been sent to ${selectedUser.email}`)
                setResetPasswordOpen(false);
                // Optionally refresh user list
                fetchUsers();
            } else {
                toast.error(response.message || 'Failed to reset password')
            }
        } catch (error: any) {
            console.error('Error resetting password:', error);
            toast.error(error || 'Failed to reset password')
        } finally {
            setResettingPassword(false);
        }
    };

    // Get status chip color
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'suspended':
                return 'error';
            case 'deactivated':
                return 'default';
            case 'deleted':
                return 'default';
            default:
                return 'default';
        }
    };

    // Filter users based on search term
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination
    const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading users...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>

            <Box sx={{ }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                            User Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage system users, roles, and permissions
                        </Typography>
                    </Box>
                </Box>

                {/* Search Bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search users by name, username or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Users Table */}
                <Paper elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'background.default' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Verified</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No users found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedUsers.map((user) => (
                                        <TableRow key={user._id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {user.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <BadgeIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {user.userName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <EmailIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <RoleIcon fontSize="small" color="primary" />
                                                    <Typography variant="body2">
                                                        {user.role?.name || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.status}
                                                    size="small"
                                                    color={getStatusColor(user.status) as any}
                                                    icon={user.status === 'active' ? <CheckCircleIcon /> : undefined}
                                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.isEmailVerified ? 'Verified' : 'Pending'}
                                                    size="small"
                                                    color={user.isEmailVerified ? 'success' : 'warning'}
                                                    sx={{ height: 24, fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip placement='top' arrow title="Edit User">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openDialog(user)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip placement='top' arrow title="Reset Password">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleResetPassword(user)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <ResetPasswordIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Box>

            {/* Add/Edit User Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 1 }
                }}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">
                                {editingUser ? 'Edit User' : 'Create New User'}
                            </Typography>
                            <IconButton onClick={closeDialog} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {/* Name */}
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: 'Name is required' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Full Name"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                    />
                                )}
                            />

                            {/* Username */}
                            <Controller
                                name="userName"
                                control={control}
                                rules={{ required: 'Username is required' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Username"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            {/* Email - Disabled in edit mode */}
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^\S+@\S+$/i,
                                        message: 'Invalid email'
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Email Address"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        disabled={!!editingUser}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            {editingUser && (
                                <FormHelperText sx={{ mt: -1, mb: 1, color: 'text.secondary' }}>
                                    Email address cannot be changed after user creation
                                </FormHelperText>
                            )}

                            {/* Phone Number */}
                            <Controller
                                name="phoneNumber"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Phone Number"
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            {/* Address Section */}
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationIcon fontSize="small" />
                                    Address Information
                                </Typography>

                                <Controller
                                    name="address.line1"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Address Line 1"
                                            size="small"
                                            sx={{ mb: 2 }}
                                        />
                                    )}
                                />

                                <Controller
                                    name="address.line2"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Address Line 2"
                                            size="small"
                                            sx={{ mb: 2 }}
                                        />
                                    )}
                                />

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.city"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="City"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.state"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="State"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.zip"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="ZIP / Postal Code"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="address.country"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Country"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Role */}
                            <FormControl fullWidth size="small" error={!!errors.role}>
                                <InputLabel>Role *</InputLabel>
                                <Controller
                                    name="role"
                                    control={control}
                                    rules={{ required: 'Role is required' }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            label="Role *"
                                        >
                                            {roles.map((role) => (
                                                <MenuItem key={role._id} value={role._id}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2">{role.name}</Typography>
                                                        {role.description && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {role.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.role && <FormHelperText>{errors.role.message}</FormHelperText>}
                            </FormControl>

                            {/* Status */}
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} label="Status">
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="suspended">Suspended</MenuItem>
                                            <MenuItem value="deactivated">Deactivated</MenuItem>
                                            <MenuItem value="deleted">Deleted</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>

                            {/* Reset Password Button in Edit Mode */}
                            {editingUser && (
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<LockOpenIcon />}
                                        onClick={() => {
                                            closeDialog();
                                            handleResetPassword(editingUser);
                                        }}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Reset User Password
                                    </Button>
                                </Box>
                            )}

                            {/* Password note for new users */}
                            {!editingUser && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="caption">
                                        New users will receive a temporary password via email. They will be prompted to change it on first login.
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={closeDialog}
                            disabled={saving}
                            size="small"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            size="small"
                        >
                            {saving ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Reset Password Confirmation Dialog */}
            <Dialog
                open={resetPasswordOpen}
                onClose={() => setResetPasswordOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ResetPasswordIcon color="warning" />
                        <Typography variant="h6" fontWeight="bold">
                            Reset User Password
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            Are you sure you want to reset the password for "{selectedUser?.name}"?
                        </Typography>
                    </Alert>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This action will:
                    </Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Generate a new temporary password for the user
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Send the new password to the user's email address
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Force the user to change their password on next login
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            Log out the user from all active sessions
                        </Typography>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                            The user will receive an email with their new temporary password and instructions to reset it.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setResetPasswordOpen(false)}
                        disabled={resettingPassword}
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={confirmResetPassword}
                        disabled={resettingPassword}
                        startIcon={resettingPassword ? <CircularProgress size={16} /> : <ResetPasswordIcon />}
                        size="small"
                    >
                        {resettingPassword ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </DialogActions>
            </Dialog>


        </>
    );
};

export default UserList;